import { createValidationResult, IModelContext, IValidationResult, ModelItem, ValidationFunction } from '@mng/model';
import { getAlterForGeburtstag } from '@mng/mng-ts-helpers';
import { UvAngebotData, UvPerson, UvPersonenData } from '../uvangebot.model.types';

export const UV_MAXIMALALTER_KIND_AUS_VERTRAG = 25;
export const UV_MAXIMALALTER_KIND = 21;

export const berufMitGefahrenklasse3Validation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  return modelContext.value && modelContext.value.gefahrenklasse === 3
    ? createValidationResult(
        'beruf-gefahrenklasse',
        modelContext.value,
        `UV von ${modelContext.value.name} mit der Gefahrenklasse 3 nicht möglich`
      )
    : null;
};

export const uvPersonenValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const uvAngebotData: UvAngebotData = modelContext.dataStack[0];
  const uvPersonenData: UvPersonenData = modelContext.value;
  const uvPersonen: UvPerson[] = uvPersonenData.personen;

  const vns: UvPerson[] = uvPersonen.filter((currentUvPerson) => currentUvPerson.personRollen.vn);
  const vps: UvPerson[] = uvPersonen.filter((currentUvPerson) => currentUvPerson.personRollen.vp);
  const kollektiv: boolean = uvAngebotData.kollektiv === true ? true : false;

  const validationErrors: string[] = [];

  if (vns.length !== 1) {
    validationErrors.push('Es muss 1 VN definiert sein');
  }

  if (!kollektiv) {
    if (vps.length === 0) {
      validationErrors.push('Es muss mindestens 1 VP definiert sein');
    }

    // Zähle nur VPs die nicht möglicherweise Kinder sein können (<21 oder <25, wenn sie im Vertrag schon Kinder waren)
    if (getVPsWithoutPotentialChildren(uvPersonen).length > 2) {
      validationErrors.push('Es dürfen nur maximal 2 VPs über 21 Jahre (bzw. 25 Jahre bei Konvertierung) alt sein');
    }

    if (vps.length > 1 && getVPsOlderThan(uvPersonen, 16).length === 0) {
      validationErrors.push('Bei mehreren VPs muss zumindest einer mindestens 16 Jahre alt sein');
    }

    const nichtKindVps = vps.filter((p) => !p.personRollen.kind);

    if (nichtKindVps.length > 2) {
      validationErrors.push('Es dürfen maximal 2 VPs erwachsen sein');
    }

    const kindVps = vps.filter((p) => p.personRollen.kind);

    if (kindVps.length > 1 && nichtKindVps.length === 0) {
      validationErrors.push('Es muss zumindest ein VP erwachsen sein');
    }
  }

  if (
    vns.some((vn) => vn.stammdaten?.adresse?.ikz !== 'AT') ||
    vps.some((vn) => vn.stammdaten?.adresse?.ikz !== 'AT')
  ) {
    validationErrors.push(
      'Es können bedingungsgemäß nur Personen mit einem Hauptwohnsitz und Lebensmittelpunkt in Österreich versichert werden. Im Falle eines Auslandsverzuges oder der Verlegung des Lebensmittelpunktes ins Ausland während der Vertragslaufzeit besteht grundsätzlich kein Versicherungsschutz und der Vertrag kann nicht weitergeführt werden.'
    );
  }

  if (vns.some((vn) => vn.stammdaten?.geschlecht === 'D') || vps.some((vn) => vn.stammdaten?.geschlecht === 'D')) {
    validationErrors.push(
      'Die Erstellung von Angeboten mit Personen mit dem Geschlecht "Divers" befindet sich in Umsetzung und wird derzeit leider noch nicht unterstützt!'
    );
  }

  return validationErrors.length === 0
    ? null
    : createValidationResult(
        'personen',
        uvPersonenData,
        validationErrors[0] // By now, only one error (the first) should be returned
      );
};

function getVPsOlderThan(uvPersonen: UvPerson[], age: number): UvPerson[] {
  return uvPersonen.filter(
    (p) => p.personRollen.vp && p.stammdaten.gebdatum && getAlterForGeburtstag(p.stammdaten.gebdatum) >= age
  );
}

function getVPsWithoutPotentialChildren(uvPersonen: UvPerson[]): UvPerson[] {
  return uvPersonen.filter((p) => {
    if (p.personRollen.vp && p.stammdaten.gebdatum) {
      const age = getAlterForGeburtstag(p.stammdaten.gebdatum);
      return p.kindInVertrag ? age >= UV_MAXIMALALTER_KIND_AUS_VERTRAG : age >= UV_MAXIMALALTER_KIND;
    }
    return false;
  });
}

export function isBerufRequiredForPerson(uvAngebotData: UvAngebotData, person: UvPerson): boolean {
  return (
    !!person.hauptwohnsitzOesterreich &&
    ((person.personRollen.vp && !person.stammdaten.juristischePerson) ||
      (uvAngebotData.kollektiv && person.personRollen.vn && person.stammdaten.juristischePerson))
  );
}
